import type { AudioProfile } from "./audio/audio-types";
import type { VideoSubject } from "./video/video-types";

/**
 * Le calage des médias d'une séance : sur quoi chaque source reçue se cale, et
 * de combien la retenir.
 *
 * # Le jeu et la parole
 *
 * Une source porte du jeu ou de la parole, et c'est son émetteur qui le dit
 * (`audioContent`, `videoContent`) :
 *
 * - **le jeu** — un instrument pris au micro, une caméra sur les mains — tombe
 *   toujours avec les notes, retenu du délai que ce poste applique aux notes des
 *   autres ;
 * - **la parole** — une voix, une caméra sur le visage — se cale comme la séance
 *   le veut (`SpeechTiming`).
 *
 * # La séance décide de la parole
 *
 * Décidé le 2026-09-15 par l'utilisateur. Un récital et un jam calent tout sur
 * les notes, la parole comprise : un visage filmé pendant un récital tombe avec
 * les notes, comme des mains. Un cours la fera passer au plus tôt, parce que
 * rien n'est pire qu'une conversation en différé : la voix et le visage d'un
 * même compte y restent calés entre eux, pour que les lèvres tombent avec les
 * mots. Avec les notes par défaut : passer au plus tôt est un choix du contexte.
 *
 * Ce qui est à ce poste n'est jamais retenu : les moteurs l'écartent avant de
 * demander ici.
 *
 * # La parole au plus tôt
 *
 * Chaque source de parole dit ce qu'elle met, sans retenue, de sa prise à ce
 * qu'on la voie ou l'entende ici : son moteur le mesure. Pour chaque compte, la
 * cible est la plus lente de ses sources mesurées, sans que la plus rapide
 * attende plus de `MAX_SPEECH_WAIT_MS`. La plus lente passe au plus tôt ; les
 * autres sont retenues jusqu'à la cible. Une source seule de son compte n'attend
 * rien.
 *
 * La cible monte tout de suite, et ne baisse qu'après `FALL_AFTER_MS` : chaque
 * déplacement coûte un fondu au son et un saut à l'image, et un son en retard
 * sur l'image se remarque bien moins qu'un son en avance.
 *
 * Rien de cela ne tourne quand la parole suit les notes : les sources de parole
 * restent connues, mais rien ne se mesure.
 *
 * # Une instance par séance
 *
 * Le moteur vidéo et le moteur audio la partagent : la voix d'un compte attend
 * son visage, que l'autre moteur mesure. `LiveSession` la crée et la donne aux
 * deux ; un moteur seul en a une à lui.
 */

/** Ce qui cale le jeu sur les notes : l'horloge de séance, et le délai que ce poste applique aux notes des autres. */
export interface MediaPlayout {
  /** À ajouter à `performance.now()` pour l'instant de séance ; `null` tant qu'il n'est pas mesuré. */
  clockOffsetMs: number | null;
  /** Le délai que ce poste applique aux notes des autres, en millisecondes. */
  remoteDelayMs: number;
}

/** Ce que porte une source : du jeu, ou de la parole. */
export type MediaContent = "play" | "speech";

/** Comment se cale la parole : avec les notes, comme le jeu, ou au plus tôt. */
export type SpeechTiming = "notes" | "asap";

/** Ce que porte un son, selon ce que prend son micro : une voix est de la parole, un instrument du jeu. */
export function audioContent(profile: AudioProfile): MediaContent {
  return profile === "voice" ? "speech" : "play";
}

/** Ce que porte une image, selon ce qu'elle filme : un visage est de la parole, des mains du jeu. */
export function videoContent(subject: VideoSubject): MediaContent {
  return subject === "face" ? "speech" : "play";
}

/**
 * Au plus, en ms, ce qu'une source de parole attend les autres de son compte.
 * Un visage décodé en logiciel peut mettre plusieurs centaines de millisecondes
 * à s'afficher, et une voix qui l'attendrait rendrait la conversation pénible :
 * passé ce seuil, la voix n'attend plus, et ce sont les lèvres qui traînent.
 */
export const MAX_SPEECH_WAIT_MS = 150;

/** Tous les combien, en ms, la cible de la parole relit ce que les sources mesurent. */
export const SPEECH_CADENCE_MS = 250;

/** Au-delà, en ms, une cible qui monte suit tout de suite. */
export const RISE_STEP_MS = 5;

/**
 * En deçà, en ms, un écart entre la parole et l'image ne se remarque pas : une
 * source à moins de cet écart de la cible passe au plus tôt, et la cible ne
 * baisse pas pour si peu.
 */
export const SPEECH_TOLERANCE_MS = 15;

/** Combien de temps, en ms, une cible plus basse doit tenir avant d'être suivie : un creux passager ne déplace rien. */
export const FALL_AFTER_MS = 2_000;

/** Une source de parole suivie, telle que son moteur la mesure. */
export interface SpeechLatency {
  /** Sa piste, unique dans la salle, son et image confondus. */
  readonly id: string;
  /** Son propriétaire : les sources d'un même compte se calent ensemble. */
  readonly owner: string;
  /** Ce qu'elle met, sans retenue, de sa prise à ce qu'on la voie ou l'entende ici, en ms ; `null` sans mesure. */
  naturalMs(): number | null;
}

/** Ce qui vient de changer. */
export interface MediaTimingChange {
  /** L'horloge de séance : ce qui part se date autrement. */
  clock: boolean;
  /** Le délai des notes, ou le calage de la parole : tout ce qui suit les notes se retient autrement. */
  notes: boolean;
  /** La retenue d'au moins une source de parole passée au plus tôt. */
  speech: boolean;
}

export interface MediaTimingOptions {
  /** Comment se cale la parole : avec les notes par défaut. */
  speech?: SpeechTiming;
  /** Tous les combien relire les mesures, en ms. */
  cadenceMs?: number;
  /** L'horloge qui date les baisses de cible. Injectée par les tests. */
  now?: () => number;
}

/** Ce que la sonde de débogage montre d'un compte qui parle. */
export interface SpeechDiagnostic {
  /** La cible tenue, en ms après la prise. */
  targetMs: number;
  /** Ce que chaque source mesurée met à venir, par piste, au dernier relevé. */
  naturals: Record<string, number>;
}

interface HeldTarget {
  valueMs: number;
  /** Depuis quand la mesure passe sous la cible tenue, tolérance comprise ; `null` sinon. */
  lowerSinceMs: number | null;
}

type ChangeListener = (change: MediaTimingChange) => void;

export class MediaTiming {
  private current: MediaPlayout = { clockOffsetMs: null, remoteDelayMs: 0 };
  private speechTiming: SpeechTiming;
  /** Les sources de parole suivies, par piste. */
  private readonly followed = new Map<string, SpeechLatency>();
  /** Au dernier relevé, ce que chaque source mesurée met à venir, par compte puis par piste. */
  private measured = new Map<string, Map<string, number>>();
  /** La cible de la parole tenue pour chaque compte, en ms après la prise. */
  private readonly targets = new Map<string, HeldTarget>();
  private readonly listeners = new Set<ChangeListener>();
  private readonly cadenceMs: number;
  private readonly now: () => number;
  private cadence: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(options: MediaTimingOptions = {}) {
    this.speechTiming = options.speech ?? "notes";
    this.cadenceMs = options.cadenceMs ?? SPEECH_CADENCE_MS;
    this.now = options.now ?? (() => performance.now());
  }

  /** L'horloge et le délai des notes posés en dernier. */
  get playout(): MediaPlayout {
    return this.current;
  }

  /** Comment se cale la parole en ce moment. */
  get speech(): SpeechTiming {
    return this.speechTiming;
  }

  /**
   * L'horloge de séance et le délai que ce poste applique aux notes des autres.
   * Prévient les moteurs de ce qui a changé ; rien si rien n'a changé.
   */
  setPlayout(playout: MediaPlayout): void {
    const next: MediaPlayout = {
      clockOffsetMs: playout.clockOffsetMs,
      remoteDelayMs: Math.max(0, playout.remoteDelayMs),
    };
    const clock = next.clockOffsetMs !== this.current.clockOffsetMs;
    const notes = next.remoteDelayMs !== this.current.remoteDelayMs;

    this.current = next;

    if (clock || notes) {
      this.notify({ clock, notes, speech: false });
    }
  }

  /**
   * Comment se cale la parole : avec les notes, ou au plus tôt. C'est la séance
   * qui en décide. Prévient les moteurs quand cela change.
   */
  setSpeechTiming(speech: SpeechTiming): void {
    if (speech === this.speechTiming || this.disposed) {
      return;
    }

    this.speechTiming = speech;
    this.targets.clear();
    this.measured = new Map();

    // Au plus tôt, les cibles partent des mesures du moment, sans attendre le
    // prochain relevé : la parole cesse de suivre les notes sur-le-champ.
    if (speech === "asap") {
      this.measure();
    }

    this.syncCadence();
    this.notify({ clock: false, notes: true, speech: true });
  }

  /** Prévient à chaque changement d'horloge ou de retenue. Rend de quoi ne plus l'être. */
  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Suit une source de parole reçue, tant qu'elle vit : quand la parole passe au
   * plus tôt, ce qu'elle met à se montrer ou à se faire entendre dit ce que les
   * autres sources de son compte attendent. Rend de quoi ne plus la suivre.
   */
  follow(source: SpeechLatency): () => void {
    if (this.disposed) {
      return () => undefined;
    }

    this.followed.set(source.id, source);
    this.syncCadence();
    this.evaluate();

    return () => {
      if (this.followed.get(source.id) !== source) {
        return;
      }

      this.followed.delete(source.id);
      this.syncCadence();
      this.evaluate();
    };
  }

  /**
   * La retenue d'une source reçue, en ms après sa prise. Le jeu, et la parole
   * calée sur les notes : le délai des notes. La parole au plus tôt : la cible de
   * son compte si elle doit l'attendre, rien si elle est la plus lente ou la
   * seule mesurée.
   */
  holdFor(content: MediaContent, owner: string, sourceId: string): number {
    if (content === "play" || this.speechTiming === "notes") {
      return this.current.remoteDelayMs;
    }

    return this.speechHold(owner, sourceId);
  }

  /** La cible de chaque compte qui parle, et ce qui la fait, pour la sonde de débogage. */
  speechDiagnostics(): Record<string, SpeechDiagnostic> {
    return Object.fromEntries(
      Array.from(this.targets, ([owner, held]) => [
        owner,
        { targetMs: held.valueMs, naturals: Object.fromEntries(this.measured.get(owner) ?? []) },
      ]),
    );
  }

  dispose(): void {
    this.disposed = true;
    this.followed.clear();
    this.syncCadence();
    this.measured = new Map();
    this.targets.clear();
    this.listeners.clear();
  }

  /** Relit les mesures quand la parole passe au plus tôt, et prévient si une retenue en change. */
  private evaluate(): void {
    if (this.speechTiming !== "asap") {
      return;
    }

    const before = this.speechHolds();

    this.measure();

    if (!sameHolds(before, this.speechHolds())) {
      this.notify({ clock: false, notes: false, speech: true });
    }
  }

  /**
   * Relit ce que chaque source met à venir, et déplace les cibles. Pour chaque
   * compte : la plus lente de ses sources mesurées, sans que la plus rapide
   * attende plus de `MAX_SPEECH_WAIT_MS`.
   */
  private measure(): void {
    const measured = new Map<string, Map<string, number>>();
    const owners = new Set<string>();

    for (const source of this.followed.values()) {
      owners.add(source.owner);

      const natural = source.naturalMs();

      if (natural === null || !Number.isFinite(natural)) {
        continue;
      }

      const group = measured.get(source.owner) ?? new Map<string, number>();

      group.set(source.id, Math.max(0, natural));
      measured.set(source.owner, group);
    }

    this.measured = measured;

    // Plus rien de suivi pour un compte : sa cible s'efface. Suivi sans mesure du
    // moment, elle reste : une mesure qui manque un instant ne dit pas que le
    // trajet a changé.
    for (const owner of Array.from(this.targets.keys())) {
      if (!owners.has(owner)) {
        this.targets.delete(owner);
      }
    }

    for (const [owner, group] of measured) {
      const naturals = Array.from(group.values());

      this.approach(owner, Math.min(Math.max(...naturals), Math.min(...naturals) + MAX_SPEECH_WAIT_MS));
    }
  }

  /** La retenue de chaque source suivie, telle que le calage du moment la donne. */
  private speechHolds(): Map<string, number> {
    return new Map(Array.from(this.followed.values(), (source) => [source.id, this.holdFor("speech", source.owner, source.id)]));
  }

  private speechHold(owner: string, sourceId: string): number {
    const target = this.targets.get(owner);
    const group = this.measured.get(owner);

    if (target === undefined || group === undefined) {
      return 0;
    }

    // Seule mesurée de son compte : elle n'a personne à attendre.
    if (!Array.from(group.keys()).some((id) => id !== sourceId)) {
      return 0;
    }

    const own = group.get(sourceId);

    // La plus lente, à la tolérance près : elle passe au plus tôt, et c'est elle
    // que les autres attendent. Une source pas encore mesurée attend la cible.
    if (own !== undefined && own >= target.valueMs - SPEECH_TOLERANCE_MS) {
      return 0;
    }

    return target.valueMs;
  }

  /**
   * Rapproche la cible tenue d'un compte de la mesure : tout de suite quand elle
   * monte, seulement après `FALL_AFTER_MS` quand elle baisse au-delà de la
   * tolérance. Un visage qui prend du retard doit être attendu sur-le-champ ; un
   * visage qui en regagne peut attendre qu'on en soit sûr.
   */
  private approach(owner: string, targetMs: number): void {
    const held = this.targets.get(owner);

    if (held === undefined || targetMs > held.valueMs + RISE_STEP_MS) {
      this.targets.set(owner, { valueMs: targetMs, lowerSinceMs: null });

      return;
    }

    if (targetMs >= held.valueMs - SPEECH_TOLERANCE_MS) {
      held.lowerSinceMs = null;

      return;
    }

    const now = this.now();

    if (held.lowerSinceMs === null) {
      held.lowerSinceMs = now;

      return;
    }

    if (now - held.lowerSinceMs >= FALL_AFTER_MS) {
      this.targets.set(owner, { valueMs: targetMs, lowerSinceMs: null });
    }
  }

  /** Relever en cadence tant qu'une source de parole est suivie et que la parole passe au plus tôt. */
  private syncCadence(): void {
    const wanted = this.followed.size > 0 && this.speechTiming === "asap" && !this.disposed;

    if (wanted && this.cadence === null) {
      this.cadence = setInterval(() => this.evaluate(), this.cadenceMs);

      return;
    }

    if (!wanted && this.cadence !== null) {
      clearInterval(this.cadence);
      this.cadence = null;
    }
  }

  private notify(change: MediaTimingChange): void {
    for (const listener of Array.from(this.listeners)) {
      listener(change);
    }
  }
}

function sameHolds(a: ReadonlyMap<string, number>, b: ReadonlyMap<string, number>): boolean {
  if (a.size !== b.size) {
    return false;
  }

  for (const [id, hold] of a) {
    if (b.get(id) !== hold) {
      return false;
    }
  }

  return true;
}
